import { ColDef, GridApi, GridReadyEvent, ICellRendererParams } from 'ag-grid-community'
import { debounce, startCase } from 'lodash'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import * as XLSX from 'xlsx'
import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Th,
  Tr,
  Text,
  Tooltip,
  useToast
} from '@chakra-ui/react'
import {
  ArrowDownOnSquareIcon,
  ArrowDownTrayIcon,
  ArrowUturnLeftIcon,
  CheckIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { useQuery } from '@tanstack/react-query'
import { BadDebtBalances } from '@homevest/types/buildium'
import {
  CashoutFinalizationValidation,
  CashoutLedgerRow,
  CashoutPendingLiabilityRow,
  DepositAccountingStatementInputs
} from '@homevest/types/cashouts'
import { BAD_DEBT_TYPES } from '@homevest/utils/buildium'
import { capabilities } from '@homevest/utils'
import { CASHOUT_STATUSES } from '@homevest/utils/cashouts'
import { RENTAL_PROGRAM_TYPES, LEASE_END_REASONS } from '@homevest/utils/rentals'

import { BaseGrid, ContentSectionCard } from 'components/TailwindUIToolkit'
import {
  CurrencyFormField,
  TextareaFormField,
  DropdownFormField
} from 'components/ChakraToolkit/FormField'
import {
  GetRentalCashoutDetailsQuery,
  useCreateCashoutPendingLiabilityMutation,
  useDeleteCashoutPendingLiabilityMutation,
  useGetCashoutPendingLiabilitiesLedgerAccountsQuery,
  useUpup_LiabilityTypesQuery
} from 'graphql/generated'
import axios from 'lib/axios'
import { hasCapability } from 'lib/admin-perms'
import { formatUTCDate } from 'lib/date-time'
import { formatMoney, formatPercent, roundToNearest } from 'lib/numbers'
import { StoreState } from 'store'
import AcceleratedRentModal from 'screens/RentRoll/Details/AcceleratedRentModal'
import {
  canFinalizeWithCashoutValidation,
  CashoutFinalizationChecks,
  getCashoutValidationSubmission
} from './CashoutFinalizationChecks'
import DepositAccountingStatement from './DepositAccountingStatement'
import UndoCashoutModal from './UndoCashoutModal'

const { ADJUSTMENTS_FINALIZED, CASHED_OUT, UTILITIES_FINALIZED } = CASHOUT_STATUSES
interface CashoutFormProps {
  data: GetRentalCashoutDetailsQuery
}

interface CashoutAmounts {
  final_wallet_amount: number
  write_off_amount: number
  clawback_amount: number
  wallet_fee_amount: number
  total_investment_amount: number
  paid_out_amount: number
  bad_debt_rent_income_adjustment: number
  bad_debt_other_income_adjustment: number
  bad_debt_turn_dispo_adjustment: number
  bad_debt_other_expenses_adjustment: number
}

interface CashoutPreview {
  summary: CashoutAmounts
  validation: CashoutFinalizationValidation
}

type AcceleratedRentAccuracy = {
  status: 'accurate' | 'needs_review' | 'not_applicable'
  reasons: Array<
    | 'CALCULATION_UNAVAILABLE'
    | 'MISSING_CHARGES'
    | 'MISMATCHED_CHARGES'
    | 'DUPLICATE_CHARGES'
    | 'UNEXPECTED_CHARGES'
  >
  expectedTotal: number | null
  postedTotal: number
  difference: number | null
  acceleratedEndDate: string | null
  nextRentalOccupancyDate: string | null
}

type CashoutStatementInputs = DepositAccountingStatementInputs & {
  acceleratedRentAccuracy: AcceleratedRentAccuracy
}

const { CASHOUTS_ADMIN, LEDGER_SUPER_EDITOR } = capabilities.CAPABILITY_TYPES

const fetchCashoutPreview = async (
  rentalId: string,
  finalWalletAmount?: number,
  walletFee?: number,
  badDebtBalances?: Partial<BadDebtBalances>
) => {
  const params: Record<string, any> = {}
  if (finalWalletAmount !== undefined) {
    params.final_wallet_amount = finalWalletAmount
  }
  if (walletFee !== undefined) {
    params.wallet_fee = walletFee
  }
  if (badDebtBalances !== undefined) {
    params.bad_debt_balance_rent_income = badDebtBalances.rent_income ?? 0
    params.bad_debt_balance_other_income = badDebtBalances.other_income ?? 0
    params.bad_debt_balance_turn_dispo = badDebtBalances.turn_dispo ?? 0
    params.bad_debt_balance_other_expenses = badDebtBalances.other_expenses ?? 0
  }
  const res = await axios.get(`/admin/rentals/${rentalId}/preview_cashout`, { params })
  return res.data
}

const getDefaultWalletFee = (walletValue?: number | null) => {
  return walletValue ? roundToNearest(walletValue * 0.1, 1, 2) : 0
}

const mapTypeToColor = (type?: string) => {
  if (type === 'liability') {
    return 'red'
  }
  if (type === 'payment' || type === 'credit') {
    return 'green'
  }
  return
}

// Columns for outstanding ledger items grid
const ledgerColumns: ColDef<CashoutLedgerRow>[] = [
  {
    headerName: 'Date',
    field: 'date',
    valueFormatter: (params) => formatUTCDate(params.value),
    filter: 'agDateColumnFilter',
    flex: 1
  },
  {
    headerName: 'Type',
    field: 'type',
    valueFormatter: (params) => startCase(params.value),
    flex: 1
  },

  {
    headerName: 'Liability Type',
    field: 'liability_type.name',
    flex: 1.1
  },
  {
    headerName: 'Amount',
    field: 'amount_remaining',
    cellStyle: (params) => {
      const color = mapTypeToColor(params.data?.type)
      if (color) {
        return { color, alignContent: 'center' }
      }
      return null
    },
    valueFormatter: (params) => {
      return formatMoney(Number(params.value), 2, '$')
    },
    flex: 1
  },
  {
    headerName: 'Notes',
    field: 'note',
    flex: 2.5
  }
]

// Columns for pending liabilities grid
const pendingLiabilityColumns: ColDef<CashoutPendingLiabilityRow>[] = [
  {
    headerName: 'Liability Type',
    field: 'liability_type.name',
    flex: 1.5
  },
  {
    headerName: 'Maintenance Category',
    field: 'ledger_account.name',
    flex: 1.5
  },
  {
    headerName: 'Amount',
    field: 'amount',
    valueFormatter: (params) => formatMoney(Number(params.value), 2, '$'),
    cellStyle: () => ({ color: 'red', alignContent: 'center' }),
    flex: 1
  },
  {
    headerName: 'Description',
    field: 'description',
    flex: 3
  }
]

type PendingLiabilityData = {
  amount: number
  liabilityTypeId: string
  ledgerAccountId: string
  description: string
}
// Modal for creating a new pending liability
const PendingLiabilityModal = ({
  cashoutId,
  isOpen,
  onClose
}: {
  cashoutId: string
  isOpen: boolean
  onClose: () => void
}) => {
  // Object containing all data needed to submit form
  const defaultPendingLiabilityData = {
    amount: 0,
    liabilityTypeId: '',
    ledgerAccountId: '',
    description: ''
  }
  const [pendingLiabilityData, setPendingLiabilityData] = useState<PendingLiabilityData>(
    defaultPendingLiabilityData
  )

  // Fetch liability types from database
  const [{ data: liabilityTypesData }] = useUpup_LiabilityTypesQuery()
  const filteredLiabilityTypes = (liabilityTypesData?.liability_types ?? [])
    .filter((liabilityType) =>
      [
        'Maintenance Chargeback - Turn Repairs',
        'Maintenance Chargeback - Disposition Repairs'
      ].includes(liabilityType.name)
    )
    .map((liabilityType) => ({
      value: liabilityType.id as string,
      label: liabilityType.name
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  // Fetch maintenance categories (ledger accounts) from database
  const [{ data: ledgerAccounts }] = useGetCashoutPendingLiabilitiesLedgerAccountsQuery()
  // Once liability type is selected, filter to the corresponding ledger accounts
  const filteredLedgerAccounts = (ledgerAccounts?.ledger_accounts ?? [])
    .filter((ledgerAccount) =>
      ledgerAccount.ledger_account_categories.some((lac) => {
        const selectedLiabilityTypeId = pendingLiabilityData.liabilityTypeId
        if (!selectedLiabilityTypeId) {
          return false
        }
        const selectedLiabilityType = liabilityTypesData?.liability_types.find(
          (lt) => lt.id === selectedLiabilityTypeId
        )?.name
        if (selectedLiabilityType === 'Maintenance Chargeback - Turn Repairs') {
          return lac.category === 'turn_r_m'
        }
        if (selectedLiabilityType === 'Maintenance Chargeback - Disposition Repairs') {
          return lac.category === 'disposition_r_m'
        }
        return false
      })
    )
    .map((ledgerAccount) => ({
      value: ledgerAccount.id as string,
      label: ledgerAccount.name
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  // Track error messages for each form field
  const defaultErrors = {
    amount: '',
    liabilityTypeId: '',
    ledgerAccountId: '',
    description: ''
  }
  const [errors, setErrors] = useState(defaultErrors)
  const validatePendingLiabilityData = (data: PendingLiabilityData) => {
    setErrors({
      amount: !data.amount || data.amount < 0 ? 'Amount must be greater than 0' : '',
      liabilityTypeId: !data.liabilityTypeId ? 'Liability type is required' : '',
      ledgerAccountId: !data.ledgerAccountId ? 'Maintenance category is required' : '',
      description: !data.description ? 'Description is required' : ''
    })
  }
  const isFormValid = Object.values(errors).every((error) => error === '')
  // Update errors whenever user updates form data
  const handleFormChange = (data: PendingLiabilityData) => {
    setPendingLiabilityData(data)
    validatePendingLiabilityData(data)
  }

  // Once user successfully submits, use this mutation to create a row in cashouts_pending_liabilities
  const [{ fetching: isMutationRunning }, createPendingLiabilityMutation] =
    useCreateCashoutPendingLiabilityMutation()
  const createPendingLiability = async (data: PendingLiabilityData) =>
    await createPendingLiabilityMutation({
      cashoutId,
      ...data
    })

  // Track any errors from the mutation
  const [createError, setCreateError] = useState<string | null>(null)

  // Reset all form data when closing modal
  const handleClose = () => {
    setPendingLiabilityData(defaultPendingLiabilityData)
    setErrors(defaultErrors)
    setCreateError(null)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Maintenance Charge</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <CurrencyFormField
              field='amount'
              label='Amount'
              isRequired
              errors={errors}
              data={pendingLiabilityData}
              setData={handleFormChange}
            />
            <DropdownFormField
              field='liabilityTypeId'
              label='Liability Type'
              isRequired
              errors={errors}
              data={pendingLiabilityData}
              setData={handleFormChange}
              options={filteredLiabilityTypes}
            />
            <DropdownFormField
              field='ledgerAccountId'
              label='Maintenance Category'
              isRequired
              errors={errors}
              data={pendingLiabilityData}
              setData={handleFormChange}
              options={filteredLedgerAccounts}
            />
            <TextareaFormField
              field='description'
              label='Description'
              isRequired={false}
              errors={errors}
              data={pendingLiabilityData}
              setData={handleFormChange}
            />
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Stack w='100%'>
            {createError && (
              <Alert fontSize='xs' status='error' mb={2}>
                Error: {createError}
              </Alert>
            )}
            <Button
              alignSelf='end'
              maxW='40%'
              colorScheme='teal'
              isDisabled={!isFormValid}
              isLoading={isMutationRunning}
              onClick={async () => {
                // Update errors one last time just in case
                validatePendingLiabilityData(pendingLiabilityData)
                if (isFormValid) {
                  const result = await createPendingLiability(pendingLiabilityData)
                  if (result.data?.insert_cashouts_pending_liabilities_one?.id) {
                    handleClose()
                  } else {
                    setCreateError(result.error?.message ?? 'Failed to create charge')
                  }
                }
              }}
            >
              Create Charge
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

// Helper function for Excel export
const convertCsvToArrayOfArrays = (csvString: string) => {
  const csvLines = csvString.split('\n')
  const headers = csvLines[0].split(',')
  const amountIndex = headers.indexOf('"AMOUNT"')

  const rows = csvLines.map((line, rowIndex) => {
    const result = []
    let current = ''
    let columnIndex = 0
    let inQuotes = false

    // Account for each field being wrapped in double quotes
    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      if (char === '"') {
        inQuotes = !inQuotes
      } else if (char === ',' && !inQuotes) {
        result.push(
          rowIndex !== 0 && columnIndex === amountIndex ? Number(current.trim()) : current.trim()
        )
        current = ''
        columnIndex++
      } else {
        current += char
      }
    }
    result.push(
      rowIndex !== 0 && columnIndex === amountIndex ? Number(current.trim()) : current.trim()
    )
    return result
  })

  return rows
}

// Main component
export const CashoutForm: FC<React.PropsWithChildren<CashoutFormProps>> = ({ data }) => {
  const { rentalId } = useParams<{ rentalId: string }>()
  const admin = useSelector((state: StoreState) => state.admin)

  const [loading, setLoading] = useState(false)
  const [isFinalizingCashout, setIsFinalizingCashout] = useState(false)
  const finalizationInProgress = useRef(false)
  const [cashoutPreview, setCashoutPreview] = useState<CashoutPreview>()
  const [suppressCommunications, setSuppressCommunications] = useState(false)
  const [suppressionReason, setSuppressionReason] = useState('')
  const [isEditingWalletValue, setIsEditingWalletValue] = useState(false)
  const [walletValueOverride, setWalletValueOverride] = useState<number | null>(null)
  const [walletFee, setWalletFee] = useState({ amount: 0 })
  const [acknowledgedValidationFingerprint, setAcknowledgedValidationFingerprint] = useState<
    string | null
  >(null)
  const [isRecheckingCashoutValidation, setIsRecheckingCashoutValidation] = useState(false)
  const [isFetchingBadDebtBalances, setIsFetchingBadDebtBalances] = useState(false)
  const [currentBadDebtBalances, setCurrentBadDebtBalances] = useState<Partial<BadDebtBalances>>({
    rent_income: undefined,
    other_income: undefined,
    turn_dispo: undefined,
    other_expenses: undefined
  })
  const [isAddPendingLiabilityModalOpen, setIsAddPendingLiabilityModalOpen] = useState(false)
  const [isUndoCashoutModalOpen, setIsUndoCashoutModalOpen] = useState(false)
  const [isAcceleratedRentModalOpen, setIsAcceleratedRentModalOpen] = useState(false)
  const [{ fetching: isDeletingPendingLiability }, deletePendingLiabilityMutation] =
    useDeleteCashoutPendingLiabilityMutation()

  const history = useHistory()
  const toast = useToast()

  const cashoutStatus = data.rentals_by_pk?.cashout?.status
  const canFinalizeAdjustments = cashoutStatus === UTILITIES_FINALIZED
  const isVanilla = data.rentals_by_pk?.rental_program_type === RENTAL_PROGRAM_TYPES.VANILLA
  const finalSharingPercentage = Number(
    data.rentals_by_pk?.current_statement?.ending_premium_factor
  )

  const [ledgerGridApi, setLedgerGridApi] = useState<GridApi>()
  const [pendingLiabilitiesGridApi, setPendingLiabilitiesGridApi] = useState<GridApi>()
  const onLedgerGridReady = (evt: GridReadyEvent) => {
    setLedgerGridApi(evt.api)
  }
  const onPendingLiabilitiesGridReady = (evt: GridReadyEvent) => {
    setPendingLiabilitiesGridApi(evt.api)
  }

  const {
    data: depositAccountingStatementInputs,
    refetch: refetchDepositAccountingStatementInputs
  } = useQuery({
    queryKey: ['deposit-accounting-statement-inputs', rentalId],
    queryFn: async () => {
      const response = await axios.get<CashoutStatementInputs>(
        `/admin/rentals/${rentalId}/deposit_accounting_statement_inputs`
      )
      return response.data
    }
  })

  const ledgerItems = useMemo(
    () => depositAccountingStatementInputs?.cashoutLedgerRows ?? [],
    [depositAccountingStatementInputs?.cashoutLedgerRows]
  )
  const pendingLiabilities = useMemo(
    () => depositAccountingStatementInputs?.cashoutPendingLiabilities ?? [],
    [depositAccountingStatementInputs?.cashoutPendingLiabilities]
  )
  const canManageAcceleratedRent = hasCapability(admin, LEDGER_SUPER_EDITOR)
  const canOverrideCashoutValidation = hasCapability(admin, CASHOUTS_ADMIN)

  const handleFetchBadDebt = async () => {
    setIsFetchingBadDebtBalances(true)
    const res = await axios.get(`/admin/rentals/${rentalId}/buildium_bad_debt_balances`)
    setCurrentBadDebtBalances(res.data as BadDebtBalances)
    setIsFetchingBadDebtBalances(false)
  }

  const cashoutButtonTooltipLabel = () => {
    if (isFinalizingCashout) {
      return 'Cashout is in progress'
    }
    if (cashoutStatus === UTILITIES_FINALIZED && !hasUpdatedBadDebtBalances) {
      return 'Please fetch updated bad debt balances before finalizing cashout'
    }
    if (cashoutStatus === UTILITIES_FINALIZED) {
      return 'Cashout is ready to be finalized'
    }
    if (cashoutStatus === ADJUSTMENTS_FINALIZED) {
      return 'Cashout has already been finalized'
    }
    if (cashoutStatus === CASHED_OUT) {
      return 'Cashout has already been paid out'
    }
    return 'Cashout is not ready to be finalized'
  }

  const debouncedFetchCashoutPreview = useMemo(() => {
    return debounce(
      async (
        rentalId: string,
        finalWalletAmount?: number,
        walletFeeAmount?: number,
        badDebtBalances?: Partial<BadDebtBalances>
      ) => {
        try {
          setLoading(true)
          const data = await fetchCashoutPreview(
            rentalId,
            finalWalletAmount,
            walletFeeAmount,
            badDebtBalances
          )
          setCashoutPreview(data)
        } catch (e: any) {
          toast({
            title: `Failed to preview cashout. ${e.message}`,
            status: 'error',
            position: 'top',
            duration: 5000
          })
        } finally {
          setLoading(false)
        }
      },
      500
    )
  }, [toast])

  // on mount or refetch, set default values for wallet fee and wallet value override
  useEffect(() => {
    setLoading(true)
    fetchCashoutPreview(rentalId).then((data) => {
      setCashoutPreview(data)
      setWalletFee({ amount: data.summary.wallet_fee_amount })
      setWalletValueOverride(data.summary.final_wallet_amount)
      setLoading(false)
    })
  }, [rentalId])

  useEffect(() => {
    setAcknowledgedValidationFingerprint(null)
  }, [cashoutPreview?.validation.fingerprint])

  useEffect(() => {
    if (isEditingWalletValue) {
      return
    }

    // need to do this because useEffect cannot be called conditionally so we can't just use finalWalletAmount
    const walletAmount = walletValueOverride ?? cashoutPreview?.summary.final_wallet_amount
    debouncedFetchCashoutPreview(rentalId, walletAmount, walletFee.amount, currentBadDebtBalances)
  }, [
    isEditingWalletValue,
    rentalId,
    walletValueOverride,
    walletFee.amount,
    currentBadDebtBalances,
    ledgerItems,
    pendingLiabilities,
    debouncedFetchCashoutPreview
  ])

  // make sure wallet fee is 0 for certain cases
  useEffect(() => {
    if (
      data?.rentals_by_pk?.lease_end_reason === LEASE_END_REASONS.PURCHASED_HOME_DISPO ||
      data?.rentals_by_pk?.rental_program_type === RENTAL_PROGRAM_TYPES.VANILLA
    ) {
      setWalletFee({ amount: 0 })
    }
  }, [
    data?.rentals_by_pk?.lease_end_reason,
    data?.rentals_by_pk?.rental_program_type,
    walletValueOverride
  ])

  if (!cashoutPreview) {
    return <Spinner />
  }

  const user = data?.rentals_by_pk?.primary_rental_user[0]?.user
  const userName = `${user?.first_name} ${user?.last_name}`
  const address = data?.rentals_by_pk?.portfolio_home?.home.address
  const address1 = address?.display_line_1 ?? ''
  const address2 = address?.display_line_2 ?? ''
  const fullAddress = `${address1} ${address2 ?? ''}`
  const isFinalized = [
    CASHOUT_STATUSES.ADJUSTMENTS_FINALIZED,
    CASHOUT_STATUSES.CASHED_OUT
  ].includes(cashoutStatus)
  const title = isFinalized ? 'Finalized Cashout' : 'Cashout Preview'

  const sumOutstandingLiabilities = ledgerItems
    .filter((item) => item.type === 'liability')
    .reduce((acc, item) => acc + item.amount_remaining, 0)
  const sumOutstandingPayments = ledgerItems
    .filter((item) => item.type === 'payment' || item.type === 'credit')
    .reduce((acc, item) => acc + item.amount_remaining, 0)
  const sumPendingLiabilities = pendingLiabilities.reduce((acc, item) => acc + item.amount, 0)
  const finalWalletAmount = Number(
    walletValueOverride ?? cashoutPreview.summary.final_wallet_amount
  )
  const totalInvestmentAmount = Number(cashoutPreview.summary.total_investment_amount)
  const paidOutAmount = Number(cashoutPreview.summary.paid_out_amount)
  const writeOffAmount = Number(cashoutPreview.summary.write_off_amount)
  const badDebtRentIncomeAdjustment = Number(cashoutPreview.summary.bad_debt_rent_income_adjustment)
  const badDebtOtherIncomeAdjustment = Number(
    cashoutPreview.summary.bad_debt_other_income_adjustment
  )
  const badDebtTurnDispoAdjustment = Number(cashoutPreview.summary.bad_debt_turn_dispo_adjustment)
  const badDebtOtherExpensesAdjustment = Number(
    cashoutPreview.summary.bad_debt_other_expenses_adjustment
  )
  const badDebtAdjustmentAmount =
    badDebtRentIncomeAdjustment +
    badDebtOtherIncomeAdjustment +
    badDebtTurnDispoAdjustment +
    badDebtOtherExpensesAdjustment
  const isBeingPaidOut = paidOutAmount > 0

  const hasUpdatedBadDebtBalances = Object.entries(currentBadDebtBalances).every(
    ([key, value]) => Object.values(BAD_DEBT_TYPES).includes(key as any) && value !== undefined
  )
  const hasActiveCashoutFlags = cashoutPreview.validation.flags.length > 0
  const hasAcknowledgedCurrentCashoutFlags =
    hasActiveCashoutFlags &&
    acknowledgedValidationFingerprint === cashoutPreview.validation.fingerprint

  const fetchLatestCashoutPreview = () =>
    fetchCashoutPreview(rentalId, finalWalletAmount, walletFee.amount, currentBadDebtBalances)

  const handleRecheckCashoutValidation = async () => {
    try {
      setIsRecheckingCashoutValidation(true)
      const latestPreview = await fetchLatestCashoutPreview()
      setCashoutPreview(latestPreview)
      const activeCheckCount =
        latestPreview.validation.flags.length + (latestPreview.validation.warnings?.length ?? 0)
      toast({
        title: activeCheckCount === 0 ? 'All cashout checks passed' : 'Cashout checks refreshed',
        description:
          activeCheckCount === 0
            ? undefined
            : `${activeCheckCount} cashout ${
                activeCheckCount === 1 ? 'check still needs' : 'checks still need'
              } attention.`,
        status: activeCheckCount === 0 ? 'success' : 'info',
        position: 'top',
        duration: 5000
      })
    } catch (e: any) {
      toast({
        title: `Failed to recheck cashout. ${e.message}`,
        status: 'error',
        position: 'top',
        duration: 5000
      })
    } finally {
      setIsRecheckingCashoutValidation(false)
    }
  }

  const handleCashoutRental = async () => {
    if (finalizationInProgress.current) {
      return
    }
    finalizationInProgress.current = true
    setIsFinalizingCashout(true)

    let toastId: ReturnType<typeof toast> | undefined
    try {
      const latestPreview = await fetchLatestCashoutPreview()
      setCashoutPreview(latestPreview)

      const latestValidation = latestPreview.validation as CashoutFinalizationValidation
      const validationSubmission = getCashoutValidationSubmission(
        latestValidation,
        acknowledgedValidationFingerprint
      )
      if (!validationSubmission) {
        setAcknowledgedValidationFingerprint(null)
        toast({
          title: 'Cashout checks need acknowledgement',
          description:
            cashoutPreview.validation.fingerprint === latestValidation.fingerprint
              ? 'Review the active checks and acknowledge them before finalizing.'
              : 'The cashout checks changed. Review the updated list and acknowledge it again.',
          status: 'warning',
          position: 'top',
          duration: 7000
        })
        return
      }

      toastId = toast({
        title: 'Cashout in progress',
        status: 'info',
        position: 'top',
        duration: null
      })
      await axios.post(`/admin/rentals/${rentalId}/finalize_adjustments`, {
        final_wallet_amount: finalWalletAmount,
        wallet_fee: walletFee.amount,
        ...validationSubmission,
        tenant_communications_suppressed: suppressCommunications,
        ...(suppressCommunications
          ? { tenant_communications_suppression_reason: suppressionReason.trim() }
          : {}),
        bad_debt_balances: currentBadDebtBalances
      })
      toast({
        id: toastId,
        title: 'Cashout Successful!',
        status: 'success',
        position: 'top',
        duration: 5000
      })

      history.replace(`/rent-roll/${rentalId}`)
    } catch (e: any) {
      const validation = e.response?.data?.validation as CashoutFinalizationValidation | undefined
      if (validation) {
        setCashoutPreview((preview) => (preview ? { ...preview, validation } : preview))
        setAcknowledgedValidationFingerprint(null)
      }
      toast({
        ...(toastId ? { id: toastId } : {}),
        title: validation ? 'Cashout checks changed' : `Cashout Failed. ${e.message}`,
        description: validation ? e.response?.data?.message : undefined,
        status: validation ? 'warning' : 'error',
        position: 'top',
        duration: 5000
      })
    } finally {
      finalizationInProgress.current = false
      setIsFinalizingCashout(false)
    }
  }

  const handleExportToXlsx = () => {
    const ledgerData =
      ledgerGridApi?.getDataAsCsv({
        allColumns: true
      }) ?? ''
    const ledgerRows = convertCsvToArrayOfArrays(ledgerData)

    const pendingLiabilitiesData =
      pendingLiabilitiesGridApi?.getDataAsCsv({
        allColumns: true
      }) ?? ''
    const pendingLiabilitiesRows = convertCsvToArrayOfArrays(pendingLiabilitiesData)

    const summaryRows = [
      ['Tenant Name', userName],
      ['Address', `${fullAddress}`],
      [''],
      [`Total ${isVanilla ? 'Security Deposit' : 'Wallet Value'}`, finalWalletAmount],
      [`Unapplied Funds`, sumOutstandingPayments],
      [`Unpaid Charges`, sumOutstandingLiabilities],
      [`Maintenance Charges`, sumPendingLiabilities],
      ...(isVanilla ? [] : [['Wallet Fee', Number(walletFee.amount)]]),
      [''],
      ['Total Refund', paidOutAmount],
      [''],
      ['Outstanding Ledger Items', '', '', '', '', '', 'Maintenance Charges']
    ]

    const rowNum = isVanilla ? '12' : '13'
    const sheet = XLSX.utils.aoa_to_sheet(summaryRows)
    XLSX.utils.sheet_add_aoa(sheet, ledgerRows, { origin: `A${rowNum}` })
    XLSX.utils.sheet_add_aoa(sheet, pendingLiabilitiesRows, { origin: `G${rowNum}` })

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, sheet, 'Summary')
    XLSX.writeFile(workbook, `cashout-${address1}-${userName}.xlsx`)
  }

  const depositAccountingStatementButton = (
    <DepositAccountingStatement
      // source some of the data inputs from the database directly
      // i.e. all the fields below that use depositAccountingStatementInputs
      fullAddress={depositAccountingStatementInputs?.fullAddress ?? ''}
      state={depositAccountingStatementInputs?.state ?? ''}
      tenantNames={depositAccountingStatementInputs?.tenantNames ?? ''}
      leaseStartDate={new Date(depositAccountingStatementInputs?.leaseStartDate ?? new Date())}
      leaseEndDate={new Date(depositAccountingStatementInputs?.leaseEndDate ?? new Date())}
      noticeGivenDate={new Date(depositAccountingStatementInputs?.noticeGivenDate ?? new Date())}
      moveOutDate={new Date(depositAccountingStatementInputs?.moveOutDate ?? new Date())}
      rentalProgramType={
        depositAccountingStatementInputs?.rentalProgramType ?? RENTAL_PROGRAM_TYPES.VANILLA
      }
      // source the rest of the data inputs from the user's entered values
      // this is needed b/c if cashout not finalized, database will not have wallet value, total refund, etc.
      // however user still needs to be able to preview the generated PDF
      isRefunded={paidOutAmount >= 0 && writeOffAmount <= 0}
      walletOrDepositValue={finalWalletAmount}
      totalRefund={paidOutAmount}
      totalBalanceOwed={writeOffAmount}
      cashoutLedgerRows={ledgerItems}
      cashoutPendingLiabilities={pendingLiabilities}
      walletFeeAmount={Number(walletFee.amount)}
    />
  )

  const headingComponent = (
    <HStack justifyContent='space-between'>
      <Heading as='h1' size='lg' my={4}>
        {title} for {userName}
      </Heading>
      <HStack justifyContent='space-between'>
        {depositAccountingStatementButton}
        <Button size='sm' colorScheme='teal' onClick={handleExportToXlsx}>
          <ArrowDownTrayIcon height={20} />
          <Text ml={1}>Download XLSX</Text>
        </Button>
      </HStack>
    </HStack>
  )

  const outstandingLedgerItemsComponent = (
    <ContentSectionCard
      title={
        <HStack>
          <div>Outstanding Ledger Items</div>
          <Tooltip
            maxWidth='400px'
            textAlign='center'
            fontSize='xs'
            label={
              <>
                <Text>
                  All outstanding ledger items - either charges that the tenant has not paid in
                  full, or payments/credits that have not been used up.
                </Text>
                <Text>To edit the ledger, please go back to Rent Roll.</Text>
              </>
            }
          >
            <InformationCircleIcon height={30} />
          </Tooltip>
          <Text fontSize='xs' ml={6}>
            Total Unpaid Charges:{' '}
            <span style={{ color: 'red' }}>{formatMoney(sumOutstandingLiabilities, 2, '$')}</span>
          </Text>
          <Text fontSize='xs' ml={2}>
            Total Unapplied Funds:{' '}
            <span style={{ color: 'green' }}>{formatMoney(sumOutstandingPayments, 2, '$')}</span>
          </Text>
        </HStack>
      }
    >
      <div className='h-[30vh]'>
        <BaseGrid
          columns={ledgerColumns}
          rowData={ledgerItems}
          onGridReady={onLedgerGridReady}
          rowAutoHeight={true}
          defaultColDefOverrides={{
            resizable: true,
            wrapHeaderText: true
          }}
        />
      </div>
    </ContentSectionCard>
  )

  const pendingLiabilitiesComponent = (
    <ContentSectionCard
      title={
        <HStack justifyContent='space-between'>
          <div>Maintenance Charges</div>
          <Tooltip
            maxWidth='400px'
            textAlign='center'
            fontSize='xs'
            label={
              <>
                <Text>Itemized breakdown of maintenance charges.</Text>
                <Text>
                  These will be posted to the tenant's ledger when the cashout is finalized.
                </Text>
              </>
            }
          >
            <InformationCircleIcon height={30} />
          </Tooltip>
          <Text fontSize='xs' ml={6}>
            Total Maintenance Charges:{' '}
            <span style={{ color: 'red' }}>{formatMoney(sumPendingLiabilities, 2, '$')}</span>
          </Text>
        </HStack>
      }
      action={
        isFinalized ? undefined : (
          <Button
            mr={2}
            colorScheme='teal'
            variant='outline'
            size='xs'
            onClick={(e) => {
              e.stopPropagation()
              setIsAddPendingLiabilityModalOpen(true)
            }}
          >
            <Text>+ Add Charge</Text>
          </Button>
        )
      }
    >
      <div className='h-[30vh]'>
        <BaseGrid<CashoutPendingLiabilityRow>
          columns={[
            ...pendingLiabilityColumns,
            {
              flex: 1,
              cellRenderer: (params: ICellRendererParams<CashoutPendingLiabilityRow>) => {
                return (
                  <Menu placement='start'>
                    <Tooltip label='Delete'>
                      <MenuButton as={Button} size='sm' variant='outline'>
                        <TrashIcon height={20} />
                      </MenuButton>
                    </Tooltip>
                    <MenuList minW={0} p={1}>
                      <MenuItem
                        as={Button}
                        minW={0}
                        p={2}
                        size='sm'
                        color='white'
                        backgroundColor='red'
                        isLoading={isDeletingPendingLiability}
                        isDisabled={isDeletingPendingLiability}
                        onClick={async () => {
                          await deletePendingLiabilityMutation({ id: params.data?.id })
                          await refetchDepositAccountingStatementInputs()
                        }}
                      >
                        Delete
                      </MenuItem>
                    </MenuList>
                  </Menu>
                )
              }
            }
          ]}
          rowData={pendingLiabilities}
          onGridReady={onPendingLiabilitiesGridReady}
          rowAutoHeight={true}
          defaultColDefOverrides={{
            resizable: true,
            wrapHeaderText: true
          }}
        />
      </div>
      <PendingLiabilityModal
        cashoutId={data.rentals_by_pk?.cashout?.id}
        isOpen={isAddPendingLiabilityModalOpen}
        onClose={() => {
          setIsAddPendingLiabilityModalOpen(false)
          refetchDepositAccountingStatementInputs()
        }}
      />
    </ContentSectionCard>
  )

  const cashoutSummaryComponent = (
    <ContentSectionCard
      title={
        <HStack>
          <div>Cashout Summary</div>
          {loading ? (
            <Spinner ml={4} />
          ) : (
            <Text fontSize='xs' ml={6}>
              Total{' '}
              {isBeingPaidOut ? (
                <>
                  Payout:{' '}
                  <span style={{ color: 'green' }}>{formatMoney(paidOutAmount, 2, '$')}</span>
                </>
              ) : (
                <>
                  Bad Debt:{' '}
                  <span style={{ color: 'red' }}>{formatMoney(writeOffAmount, 2, '$')}</span>
                </>
              )}
            </Text>
          )}
        </HStack>
      }
    >
      <HStack mx={4} mt={4} mb={6} spacing={12} alignItems='start'>
        {!isVanilla && (
          <Stack>
            <Table size='sm'>
              <Tbody>
                <Tr>
                  <Td>Total Contributions</Td>
                  <Td>{formatMoney(totalInvestmentAmount, 2, '$')}</Td>
                </Tr>
                <Tr>
                  <Td>Total Earnings</Td>
                  <Td>{formatMoney(finalWalletAmount - totalInvestmentAmount, 2, '$')}</Td>
                </Tr>
                <Tr>
                  <Td>Final Sharing Percentage</Td>
                  <Td>{formatPercent(finalSharingPercentage ?? null, 4)}</Td>
                </Tr>
              </Tbody>
            </Table>
            <div className='ml-3'>
              <CurrencyFormField
                label='Wallet Fee'
                field='amount'
                isRequired={false}
                isDisabled={isFinalized}
                errors={{ amount: '' }}
                data={walletFee}
                setData={setWalletFee}
                min={0}
                max={finalWalletAmount}
                formLabelProps={{
                  fontSize: 'sm',
                  alignContent: 'center',
                  my: '0px'
                }}
                formControlProps={{ display: 'flex' }}
                inputGroupProps={{ size: 'sm', ml: '100px', maxW: '150px' }}
                inputProps={{
                  onWheel: (e: any) => e.target.blur() // prevent input changing on scroll
                }}
              />
            </div>
          </Stack>
        )}
        <Stack>
          {loading ? (
            <Spinner alignSelf='center' ml={4} />
          ) : (
            <Table size='sm'>
              <Tbody>
                <Tr>
                  <Td>Total {isVanilla ? 'Security Deposit' : 'Wallet Value'}</Td>
                  <Td>
                    {isEditingWalletValue ? (
                      <HStack>
                        <Input
                          size='sm'
                          type='number'
                          maxW='150px'
                          value={walletValueOverride ?? 0}
                          onChange={(e) => {
                            const nextWalletValue = Number(e.target.value)
                            setWalletValueOverride(nextWalletValue)
                            if (!isVanilla) {
                              setWalletFee({ amount: getDefaultWalletFee(nextWalletValue) })
                            }
                          }}
                        />
                        <Button
                          size='xs'
                          colorScheme='teal'
                          variant='outline'
                          onClick={() => {
                            setIsEditingWalletValue(!isEditingWalletValue)
                            // if Up&Up rental, reset wallet fee to 10% of newly entered value
                            if (!isVanilla) {
                              setWalletFee({
                                amount: getDefaultWalletFee(walletValueOverride)
                              })
                            }
                          }}
                        >
                          <CheckIcon height={20} />
                        </Button>
                      </HStack>
                    ) : (
                      <HStack>
                        <Text>{formatMoney(finalWalletAmount, 2, '$')}</Text>
                        {!isFinalized && (
                          <Button
                            size='xs'
                            variant='outline'
                            onClick={() => setIsEditingWalletValue(!isEditingWalletValue)}
                          >
                            <PencilIcon height={20} />
                          </Button>
                        )}
                      </HStack>
                    )}
                  </Td>
                </Tr>
                <Tr>
                  <Td>Unapplied Funds</Td>
                  <Td color='green'>{formatMoney(sumOutstandingPayments, 2, '$')}</Td>
                </Tr>
                <Tr>
                  <Td>Unpaid Charges</Td>
                  <Td color='red'>{formatMoney(sumOutstandingLiabilities, 2, '$')}</Td>
                </Tr>
                <Tr>
                  <Td>Maintenance Charges</Td>
                  <Td color='red'>{formatMoney(sumPendingLiabilities, 2, '$')}</Td>
                </Tr>
                {!isVanilla && (
                  <Tr>
                    <Td>Wallet Fee</Td>
                    <Td color='red'>
                      {formatMoney(Number(cashoutPreview.summary.wallet_fee_amount), 2, '$')}
                    </Td>
                  </Tr>
                )}
                <Tr fontWeight='bold'>
                  <Td>Total {isBeingPaidOut ? 'Refund' : 'Bad Debt'}</Td>
                  <Td>{formatMoney(isBeingPaidOut ? paidOutAmount : writeOffAmount, 2, '$')}</Td>
                </Tr>
              </Tbody>
            </Table>
          )}
        </Stack>
      </HStack>
    </ContentSectionCard>
  )

  const finalizeCashoutComponent = (
    <Stack spacing={4} mx={3}>
      <Stack>
        <HStack>
          <Text fontWeight='bold'>Current Bad Debt Balances (Buildium)</Text>
          <Button
            ml={2}
            size='xs'
            colorScheme='teal'
            variant='outline'
            onClick={handleFetchBadDebt}
          >
            <ArrowDownOnSquareIcon height={20} />
            <Text ml={1}>Fetch Updated Buildium Balances</Text>
          </Button>
        </HStack>
        {isFetchingBadDebtBalances ? (
          <Spinner mx={4} my={2} />
        ) : (
          <Table size='sm' maxW='1000px'>
            <Tbody>
              <Tr>
                <Th>Bad Debt Type</Th>
                <Th>Current Balance</Th>
                <Th>Adjustment to be Posted Today</Th>
              </Tr>
              <Tr>
                <Td>Bad Debt - Rent Income</Td>
                <Td>{formatMoney(Number(currentBadDebtBalances.rent_income), 2, '$')}</Td>
                <Td
                  fontWeight='bold'
                  color={
                    badDebtRentIncomeAdjustment > 0
                      ? 'red'
                      : badDebtRentIncomeAdjustment < 0
                      ? 'green'
                      : 'black'
                  }
                >
                  {hasUpdatedBadDebtBalances && formatMoney(badDebtRentIncomeAdjustment, 2, '$')}
                </Td>
              </Tr>
              <Tr>
                <Td>Bad Debt - Other Income</Td>
                <Td>{formatMoney(Number(currentBadDebtBalances.other_income), 2, '$')}</Td>
                <Td
                  fontWeight='bold'
                  color={
                    badDebtOtherIncomeAdjustment > 0
                      ? 'red'
                      : badDebtOtherIncomeAdjustment < 0
                      ? 'green'
                      : 'black'
                  }
                >
                  {hasUpdatedBadDebtBalances && formatMoney(badDebtOtherIncomeAdjustment, 2, '$')}
                </Td>
              </Tr>
              <Tr>
                <Td>Bad Debt - Turn/Dispo Chargebacks</Td>
                <Td>{formatMoney(Number(currentBadDebtBalances.turn_dispo), 2, '$')}</Td>
                <Td
                  fontWeight='bold'
                  color={
                    badDebtTurnDispoAdjustment > 0
                      ? 'red'
                      : badDebtTurnDispoAdjustment < 0
                      ? 'green'
                      : 'black'
                  }
                >
                  {hasUpdatedBadDebtBalances && formatMoney(badDebtTurnDispoAdjustment, 2, '$')}
                </Td>
              </Tr>
              <Tr>
                <Td>Bad Debt - Other Expenses</Td>
                <Td>{formatMoney(Number(currentBadDebtBalances.other_expenses), 2, '$')}</Td>
                <Td
                  fontWeight='bold'
                  color={
                    badDebtOtherExpensesAdjustment > 0
                      ? 'red'
                      : badDebtOtherExpensesAdjustment < 0
                      ? 'green'
                      : 'black'
                  }
                >
                  {hasUpdatedBadDebtBalances && formatMoney(badDebtOtherExpensesAdjustment, 2, '$')}
                </Td>
              </Tr>
            </Tbody>
          </Table>
        )}
      </Stack>
      {!isFinalized && (
        <Stack
          as='section'
          aria-labelledby='cashout-notifications-heading'
          spacing={3}
          borderWidth='1px'
          borderColor='gray.200'
          borderRadius='md'
          bg='white'
          p={4}
        >
          <Heading as='h3' size='sm' id='cashout-notifications-heading'>
            Tenant notifications
          </Heading>
          <Checkbox
            isChecked={suppressCommunications}
            onChange={(event) => setSuppressCommunications(event.target.checked)}
          >
            Turn off tenant notifications for this cashout
          </Checkbox>
          <Text fontSize='sm' color='gray.600' pl={6}>
            This turns off the deposit-statement email and cashout email for this cashout only.
            Required physical and legal notices are unchanged.
          </Text>
          {suppressCommunications && (
            <FormControl isRequired pl={6}>
              <FormLabel htmlFor='cashout-suppression-reason' fontSize='sm'>
                Reason for turning off notifications
              </FormLabel>
              <Input
                id='cashout-suppression-reason'
                aria-label='Reason for turning off notifications'
                placeholder='Required reason'
                value={suppressionReason}
                maxLength={2000}
                onChange={(event) => setSuppressionReason(event.target.value)}
              />
            </FormControl>
          )}
        </Stack>
      )}
      <CashoutFinalizationChecks
        validation={cashoutPreview.validation}
        canOverride={canOverrideCashoutValidation}
        acknowledged={hasAcknowledgedCurrentCashoutFlags}
        onAcknowledgedChange={(acknowledged) =>
          setAcknowledgedValidationFingerprint(
            acknowledged ? cashoutPreview.validation.fingerprint : null
          )
        }
        onRecheck={handleRecheckCashoutValidation}
        isRechecking={isRecheckingCashoutValidation}
        onReviewAcceleratedRent={() =>
          canManageAcceleratedRent
            ? setIsAcceleratedRentModalOpen(true)
            : history.push(`/rent-roll/${rentalId}`)
        }
        acceleratedRentActionLabel={
          canManageAcceleratedRent ? 'Review accelerated rent' : 'View rental ledger'
        }
      />
      <HStack borderTopWidth='1px' borderColor='gray.200' pt={4}>
        <Tooltip shouldWrapChildren textAlign='center' label={cashoutButtonTooltipLabel()}>
          <Button
            onClick={handleCashoutRental}
            colorScheme={isBeingPaidOut ? 'green' : 'red'}
            size='md'
            isLoading={loading || isFinalizingCashout}
            loadingText={isFinalizingCashout ? 'Cashout in progress' : undefined}
            isDisabled={
              isFinalizingCashout ||
              !canFinalizeAdjustments ||
              (suppressCommunications && !suppressionReason.trim()) ||
              !hasUpdatedBadDebtBalances ||
              !canFinalizeWithCashoutValidation(
                cashoutPreview.validation,
                canOverrideCashoutValidation,
                acknowledgedValidationFingerprint
              )
            }
            width='100%'
          >
            {isBeingPaidOut ? (
              <>
                Cashout {formatMoney(paidOutAmount, 2, '$')} to {userName}
              </>
            ) : (
              <>
                Post {formatMoney(badDebtAdjustmentAmount, 2, '$')} of Bad Debt for {userName}
              </>
            )}
          </Button>
        </Tooltip>
      </HStack>
      {isFinalized && (
        <Button
          size='md'
          maxW='400px'
          w='100%'
          colorScheme='red'
          variant='outline'
          onClick={() => setIsUndoCashoutModalOpen(true)}
        >
          <ArrowUturnLeftIcon height={24} />
          <Text ml={2}>Undo Cashout</Text>
        </Button>
      )}
    </Stack>
  )

  return (
    <Stack w='100%' margin='auto' px='auto' maxWidth='1500px'>
      {headingComponent}
      <Stack spacing={4}>
        {outstandingLedgerItemsComponent}
        {pendingLiabilitiesComponent}
        {cashoutSummaryComponent}
      </Stack>
      <Text my={1} />
      {finalizeCashoutComponent}
      <UndoCashoutModal
        rentalId={rentalId}
        isOpen={isUndoCashoutModalOpen}
        onClose={() => setIsUndoCashoutModalOpen(false)}
      />
      <AcceleratedRentModal
        rentalId={rentalId}
        isOpen={isAcceleratedRentModalOpen}
        onClose={() => setIsAcceleratedRentModalOpen(false)}
        onSuccess={async () => {
          await refetchDepositAccountingStatementInputs()
        }}
      />
    </Stack>
  )
}
